import React from 'react';
import Button from '../ui/Button';
import { Globe, CalendarDays, Send, ArrowRight } from 'lucide-react';
import ScrollReveal from '../ui/ScrollReveal';
import FlowLine from '../ui/FlowLine';
const PlanSection = () => {
  return <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#3D52A0] text-white relative overflow-hidden" id="how-it-works" data-id="element-351">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#EDE8F5]/10 to-transparent" data-id="element-352"></div>
      <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-[#7091E6] rounded-full filter blur-3xl opacity-20" data-id="element-353"></div>
      <div className="absolute -top-10 -left-10 w-64 h-64 bg-[#EDE8F5] rounded-full filter blur-3xl opacity-10" data-id="element-354"></div>
      <div className="max-w-7xl mx-auto relative z-10" data-id="element-355">
        <ScrollReveal data-id="element-356">
          <div className="flex flex-col items-center mb-16" data-id="element-357">
            <span className="inline-block px-4 py-1 bg-white/10 rounded-full text-sm font-medium text-white mb-6 backdrop-blur-sm" data-id="element-358">
              How It Works
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-center" data-id="element-359">
              Three easy steps to maximize your outreach.
            </h2>
            <div className="w-24 h-1 bg-white/30 rounded-full" data-id="element-360"></div>
          </div>
        </ScrollReveal>
        <div className="relative" data-id="element-361">
          {/* Connecting line between steps */}
          <div className="absolute top-1/2 left-0 w-full h-0.5 bg-white/10 hidden md:block" data-id="element-362"></div>
          <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-363">
            <ScrollReveal delay={100} data-id="element-364">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 relative z-10 h-full transform hover:-translate-y-2" data-id="element-365">
                <div className="bg-white/20 p-4 rounded-full w-fit mb-6" data-id="element-366">
                  <Globe className="text-white h-8 w-8" data-id="element-367" />
                </div>
                <h3 className="text-xl font-semibold mb-4 flex items-center" data-id="element-368">
                  <span className="bg-white text-[#3D52A0] w-10 h-10 rounded-full flex items-center justify-center mr-3 shadow-lg" data-id="element-369">
                    1
                  </span>
                  Enter your church website
                </h3>
                <p className="text-white/80 mb-6" data-id="element-370">
                  Shepherd Flow AI learns your tone, colors, and brand to create
                  personalized communication that feels authentic and on
                  mission.
                </p>
                {/* Step detail */}
                <div className="mt-auto pt-4 border-t border-white/10" data-id="element-371">
                  <div className="flex items-center text-white/70 text-sm" data-id="element-372">
                    <span className="text-white/60 mr-2" data-id="element-373">•</span>
                    <span data-id="element-374">Setup takes 30 seconds</span>
                  </div>
                </div>
                {/* Arrow pointing to next step (visible on desktop) */}
                <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 hidden md:block" data-id="element-375">
                  <ArrowRight className="text-white/40 h-8 w-8" data-id="element-376" />
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={300} data-id="element-377">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 relative z-10 h-full transform hover:-translate-y-2" data-id="element-378">
                <div className="bg-white/20 p-4 rounded-full w-fit mb-6" data-id="element-379">
                  <CalendarDays className="text-white h-8 w-8" data-id="element-380" />
                </div>
                <h3 className="text-xl font-semibold mb-4 flex items-center" data-id="element-381">
                  <span className="bg-white text-[#3D52A0] w-10 h-10 rounded-full flex items-center justify-center mr-3 shadow-lg" data-id="element-382">
                    2
                  </span>
                  Add your event details
                </h3>
                <p className="text-white/80 mb-6" data-id="element-383">
                  Tell the system what's happening and what you want to
                  achieve—like "Invite families back next Sunday."
                </p>
                {/* Step detail */}
                <div className="mt-auto pt-4 border-t border-white/10" data-id="element-384">
                  <div className="flex items-center text-white/70 text-sm" data-id="element-385">
                    <span className="text-white/60 mr-2" data-id="element-386">•</span>
                    <span data-id="element-387">Simple guided setup</span>
                  </div>
                </div>
                {/* Arrow pointing to next step (visible on desktop) */}
                <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 hidden md:block" data-id="element-388">
                  <ArrowRight className="text-white/40 h-8 w-8" data-id="element-389" />
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={500} data-id="element-390">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 relative z-10 h-full transform hover:-translate-y-2" data-id="element-391">
                <div className="bg-white/20 p-4 rounded-full w-fit mb-6" data-id="element-392">
                  <Send className="text-white h-8 w-8" data-id="element-393" />
                </div>
                <h3 className="text-xl font-semibold mb-4 flex items-center" data-id="element-394">
                  <span className="bg-white text-[#3D52A0] w-10 h-10 rounded-full flex items-center justify-center mr-3 shadow-lg" data-id="element-395">
                    3
                  </span>
                  Get ready-to-send campaigns
                </h3>
                <p className="text-white/80 mb-6" data-id="element-396">
                  Instantly receive Sign-Up, Show-Up, and Follow-Up messages,
                  written in your voice and ready to send across every channel.
                </p>
                {/* Step detail */}
                <div className="mt-auto pt-4 border-t border-white/10" data-id="element-397">
                  <div className="flex items-center text-white/70 text-sm" data-id="element-398">
                    <span className="text-white/60 mr-2" data-id="element-399">•</span>
                    <span data-id="element-400">Fully customizable templates—completely free</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
        <ScrollReveal delay={700} data-id="element-401">
          <div className="flex justify-center" data-id="element-402">
            <a href="https://app.shepherdflow.ai/signup" data-id="element-403">
              <Button variant="accent" size="lg" icon={<ArrowRight size={18} data-id="element-405" />} className="bg-white text-[#3D52A0] hover:bg-[#EDE8F5]" data-id="element-404">
                Start Free
              </Button>
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>;
};
export default PlanSection;